/**
 * Photo Gallery Component
 * 
 * Horizontal scrolling gallery with camera + library add buttons.
 * Shows photo thumbnails, geolocation badges, and upload progress.
 * Supports full-screen viewing with delete and caption editing.
 */

import React, { useCallback, useState } from 'react';
import {
  View, Text, ScrollView, Pressable,
  ActivityIndicator, Image, Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, space, icon } from '../../../shared/tokens/mobile';
import { OpportunityPhoto, authApi } from '../../../shared/utils/api';
import { useOpportunityPhotos } from '../../../hooks/useOpportunityPhotos';
import { styles } from './photoGalleryStyles';

interface PhotoGalleryProps {
  portfolioId: number;
  opportunityId: number;
}

export default function PhotoGallery({ portfolioId, opportunityId }: PhotoGalleryProps) {
  const {
    photos,
    loading,
    uploading,
    error,
    pickFromLibrary,
    takePhoto,
    deletePhoto,
  } = useOpportunityPhotos({ portfolioId, opportunityId });

  const [viewingPhoto, setViewingPhoto] = useState<(OpportunityPhoto & { image_data?: string }) | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handlePhotoPress = useCallback(async (photo: OpportunityPhoto) => {
    setLoadingFull(true);
    try {
      const response = await authApi.getOpportunityPhoto(portfolioId, opportunityId, photo.id);
      if (response.success) {
        setViewingPhoto(response.photo);
      }
    } catch {
      // silently fail - user can retry
    } finally {
      setLoadingFull(false);
    }
  }, [portfolioId, opportunityId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (confirmDeleteId == null) return;
    setDeleting(true);
    try {
      await deletePhoto(confirmDeleteId);
      setConfirmDeleteId(null);
      setViewingPhoto(null);
    } catch {
      // error handled by hook
    } finally {
      setDeleting(false);
    }
  }, [confirmDeleteId, deletePhoto]);

  const handleAdd = useCallback(() => {
    setShowAddMenu(true);
  }, []);

  const handlePickFromLibrary = useCallback(() => {
    setShowAddMenu(false);
    pickFromLibrary();
  }, [pickFromLibrary]);

  const handleTakePhoto = useCallback(() => {
    setShowAddMenu(false);
    takePhoto();
  }, [takePhoto]);

  const hasPhotos = photos.length > 0;

  if (loading) {
    return (
      <View style={styles.heroEmpty}>
        <ActivityIndicator size="small" color={color.textMuted} />
      </View>
    );
  }

  return (
    <View>
      {hasPhotos ? (
        <View style={styles.galleryContainer}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.galleryScroll}
          >
            {photos.map(photo => (
              <Pressable
                key={photo.id}
                style={styles.photoCard}
                onPress={() => handlePhotoPress(photo)}
              >
                {photo.thumbnail_data ? (
                  <Image
                    source={{ uri: `data:image/jpeg;base64,${photo.thumbnail_data}` }}
                    style={styles.photoThumbnail}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={styles.photoPlaceholder}>
                    <Ionicons name="image" size={icon.lg} color={color.textMuted} />
                  </View>
                )}
                {photo.latitude && photo.longitude && (
                  <View style={styles.geoBadge}>
                    <Ionicons name="location" size={10} color={color.surface} />
                  </View>
                )}
                {photo.caption ? (
                  <View style={styles.captionBar}>
                    <Text style={styles.photoCaption} numberOfLines={1}>{photo.caption}</Text>
                  </View>
                ) : null}
              </Pressable>
            ))}

            <Pressable style={styles.addCard} onPress={handleAdd}>
              {uploading ? (
                <ActivityIndicator size="small" color={color.primary} />
              ) : (
                <>
                  <Ionicons name="add-circle-outline" size={icon.xl} color={color.primary} />
                  <Text style={styles.addText}>Add Photo</Text>
                </>
              )}
            </Pressable>
          </ScrollView>

          <View style={styles.countRow}>
            <Ionicons name="images-outline" size={14} color={color.textMuted} />
            <Text style={styles.countText}>{photos.length} photo{photos.length !== 1 ? 's' : ''}</Text>
            {error && <Text style={styles.errorText}>{error}</Text>}
          </View>
        </View>
      ) : (
        <Pressable style={styles.heroEmpty} onPress={handleAdd} disabled={uploading}>
          {uploading ? (
            <>
              <ActivityIndicator size="large" color={color.primary} />
              <Text style={styles.heroUploadText}>Uploading...</Text>
            </>
          ) : (
            <>
              <Ionicons name="camera-outline" size={icon.xxl} color={color.primary} />
              <Text style={styles.heroTitle}>Add Photos</Text>
              <Text style={styles.heroSubtitle}>Capture site photos with GPS location</Text>
            </>
          )}
        </Pressable>
      )}

      {/* Add Menu (bottom sheet) */}
      <Modal visible={showAddMenu} transparent animationType="fade" onRequestClose={() => setShowAddMenu(false)}>
        <Pressable style={styles.menuOverlay} onPress={() => setShowAddMenu(false)}>
          <View style={styles.menuCard}>
            <Text style={styles.menuTitle}>Add Photos</Text>

            <Pressable style={styles.menuOption} onPress={handleTakePhoto}>
              <Ionicons name="camera-outline" size={icon.lg} color={color.primary} />
              <View style={styles.menuOptionText}>
                <Text style={styles.menuOptionTitle}>Take Photo</Text>
                <Text style={styles.menuOptionSubtitle}>Open camera with GPS tagging</Text>
              </View>
            </Pressable>

            <Pressable style={styles.menuOption} onPress={handlePickFromLibrary}>
              <Ionicons name="images-outline" size={icon.lg} color={color.primary} />
              <View style={styles.menuOptionText}>
                <Text style={styles.menuOptionTitle}>Choose from Library</Text>
                <Text style={styles.menuOptionSubtitle}>Select multiple photos</Text>
              </View>
            </Pressable>

            <Pressable style={styles.menuCancel} onPress={() => setShowAddMenu(false)}>
              <Text style={styles.menuCancelText}>Cancel</Text>
            </Pressable>
          </View>
        </Pressable>
      </Modal>

      {/* Full Photo Viewer (with delete confirmation nested inside) */}
      <Modal visible={!!viewingPhoto} transparent animationType="fade" onRequestClose={() => setViewingPhoto(null)}>
        <View style={styles.viewerOverlay}>
          <View style={styles.viewerHeader}>
            <Pressable onPress={() => setViewingPhoto(null)} style={styles.viewerClose}>
              <Ionicons name="close" size={icon.lg} color={color.surface} />
            </Pressable>
            <Pressable
              onPress={() => viewingPhoto && setConfirmDeleteId(viewingPhoto.id)}
              style={styles.viewerDelete}
            >
              <Ionicons name="trash-outline" size={icon.md} color={color.error} />
            </Pressable>
          </View>

          <View style={styles.viewerContent}>
            {loadingFull ? (
              <ActivityIndicator size="large" color={color.surface} />
            ) : viewingPhoto?.image_data ? (
              <Image
                source={{ uri: `data:${viewingPhoto.mime_type};base64,${viewingPhoto.image_data}` }}
                style={styles.viewerImage}
                resizeMode="contain"
              />
            ) : (
              <View style={styles.viewerPlaceholder}>
                <Ionicons name="image-outline" size={icon.xxl} color={color.surface} />
              </View>
            )}
          </View>

          {viewingPhoto && (
            <View style={styles.viewerFooter}>
              {viewingPhoto.caption && (
                <Text style={styles.viewerCaption}>{viewingPhoto.caption}</Text>
              )}
              <View style={styles.viewerMeta}>
                {viewingPhoto.latitude && viewingPhoto.longitude && (
                  <View style={styles.viewerMetaItem}>
                    <Ionicons name="location-outline" size={14} color={color.surface} />
                    <Text style={styles.viewerMetaText}>
                      {viewingPhoto.latitude.toFixed(5)}, {viewingPhoto.longitude.toFixed(5)}
                    </Text>
                  </View>
                )}
                <View style={styles.viewerMetaItem}>
                  <Ionicons name="time-outline" size={14} color={color.surface} />
                  <Text style={styles.viewerMetaText}>
                    {new Date(viewingPhoto.created_at).toLocaleDateString()}
                  </Text>
                </View>
                {viewingPhoto.uploader_name && (
                  <View style={styles.viewerMetaItem}>
                    <Ionicons name="person-outline" size={14} color={color.surface} />
                    <Text style={styles.viewerMetaText}>{viewingPhoto.uploader_name}</Text>
                  </View>
                )}
              </View>
            </View>
          )}

          {/* Delete confirmation rendered INSIDE the viewer modal to avoid stacking issues */}
          {confirmDeleteId != null && (
            <View style={styles.confirmOverlay}>
              <View style={styles.confirmCard}>
                <Ionicons name="warning-outline" size={icon.xl} color={color.error} style={{ alignSelf: 'center', marginBottom: space.sm }} />
                <Text style={styles.confirmTitle}>Delete Photo</Text>
                <Text style={styles.confirmMessage}>This photo will be permanently removed.</Text>
                <View style={styles.confirmActions}>
                  <Pressable
                    style={[styles.confirmBtn, styles.confirmCancelBtn]}
                    onPress={() => setConfirmDeleteId(null)}
                    disabled={deleting}
                  >
                    <Text style={styles.confirmCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.confirmBtn, styles.confirmDeleteBtn]}
                    onPress={handleDeleteConfirm}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <ActivityIndicator size="small" color={color.surface} />
                    ) : (
                      <Text style={styles.confirmDeleteText}>Delete</Text>
                    )}
                  </Pressable>
                </View>
              </View>
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
}

